import { h, Component } from 'preact';
import { Icon, navItemIsCurrent } from '@cube/blocks';
import { trackProps, cache, DIMENSION } from '@cube/track';
import { withConfig } from '../../config';
import { Text, Localizer } from 'preact-i18n';
import cx from 'classnames';
import { Restriction } from '@cube/restrictions';

/**
 * Navigate to a specified URL in a browser tab.
 *
 * @param {String} url Value to be used to navigate the current browser tab to.
 */
export function navigateTo(url) {
	window.location.href=url;
}

/**
 * Open a new browser tab and navigate to the {url} provided.
 *
 * @param {String} url Value to be used to open a new browser window with.
 */
export function openUrl(url) {
	window.open(url, '_blank', 'noopener');
}

export function linkEventImage(link, { theme, ...props }={}) {
	if (!link) return;

	if (link.image) {
		const imageSource = link.image[theme] || link.image.default || link.image.light || link.image;

		// Check if we have srcset data for high-resolution support
		if (link.image.srcset) {
			const srcsetData = link.image.srcset[theme] || link.image.srcset.default || link.image.srcset;
			if (srcsetData && srcsetData['1x'] && srcsetData['2x']) {
				return (
					<img
						src={srcsetData['1x']}
						srcSet={`${srcsetData['1x']} 1x, ${srcsetData['2x']} 2x`}
						{...props}
					/>
				);
			}
		}

		// Fallback to regular image if no srcset
		return <img src={imageSource} {...props} />;
	}
}

export function linkEventIcon(link, props={}) {
	if (!link) return;
	return link.iconName && <Icon name={link.iconName} {...props} />;
}

export function hasLinkText(link) {
	if (!link) return;
	return link.labelId || link.label || link.text || typeof link === 'string';
}

export function getLinkText(link) {
	if (!link) return;
	return link.labelId ? <Text id={link.labelId}>{link.label || link.text}</Text> : (link.label || link.text || link);
}

export function linkEventLabel(link, props={}) {
	if (!link) return;
	return <span {...props}>{getLinkText(link)}</span>;
}

function processLinkReplacements(link) {
	if (!link || !link.replace) return link;
	return link.replace('[[TIMESTAMP]]', Math.floor(Date.now() / 1000));
}

function LinkEventView({ link, theme, displayImage = true, displayIcon = true, displayLabel = true }) {
	if (!link) return;
	let hasImage = displayImage && !!link.image;
	let hasIcon = displayIcon && !!link.iconName;
	return hasImage ? linkEventImage(link, { theme }) : (
		(hasIcon
			? (<div>{linkEventIcon(link)}{displayLabel && linkEventLabel(link)}</div>)
			: displayLabel && linkEventLabel(link)
		)
	);
}

export function linkEventAriaLabel(link) {
	if (!link) return null;
	if (link.description || link.descriptionId) return link.descriptionId ? <Text id={link.descriptionId}>{link.description}</Text> : link.description;
	return getLinkText(link);
}

export function processLinkEvent(link, e, mock) {
	const event = link.event;
	switch (event) {
		case 'external':
			break;
		case 'noop':
			break;
		default:
			if (link.url) {
				if (!mock)navigateTo(link.url);
			}
			else {
				return event;
			}
			break;
	}
}

/**
 * Returns props to apply to the a tag for a link that carries an event.
 */
function linkEventProps(link, mock) {
	if (!link || !link.event) return;
	const event = link.event;
	switch (event) {
		case 'external':
			return !mock && { target: '_blank', href: processLinkReplacements(link.url), rel: 'noopener noreferrer' };
		default:
			break;
	}
}

function getLinkEvents(linkEvents, filterFn) {
	return linkEvents.reduce((acc, linkEvent) => {
		if (filterFn(linkEvent))acc.push(linkEvent);
		if (linkEvent.subnav)acc.push(...getLinkEvents(linkEvent.subnav, filterFn));
		return acc;
	}, []);
}

export function getConfigForPath(path, linkEvents) {
	if (!linkEvents || !path) return;
	let themedLinks = getLinkEvents(
		linkEvents,
		linkEvent => linkEvent.config && navItemIsCurrent(linkEvent, path)
	).reverse();
	return themedLinks.length && themedLinks[0].config;
}

/**
 * Will return true if the click event was an actual mouse click as opposed to pressing the enter key.
 * @param {Event} e A click event.
 */
function isActualClickEvent(e) {
	//If the event has screen positions that are 0, it's most likely not a mouse event:
	return !(e.screenX === 0 && e.screenY === 0);
}

export class LinkEvent extends Component {

	handleLinkRef = (element) => {
		this.addFocusListeners(element);
		this.linkElement = element;
	}

	handleFocus = e => {
		this.setState({ focused: true });
	}

	handleBlur = () => {
		this.setState({ focused: false });
	}

	addFocusListeners(element) {
		this.clearFocusListeners();
		if (element && element.addEventListener){
			element.addEventListener('focusin', this.handleFocus);
			element.addEventListener('focusout', this.handleBlur);
		}
	}

	clearFocusListeners() {
		if (this.linkElement) {
			this.linkElement.removeEventListener('focusin', this.handleFocus);
			this.linkElement.removeEventListener('focusout', this.handleBlur);
		}
	}

	handleClick = e => {

		if (this.props.suppressLink || !this.props.link.url || (this.props.isMenuItem && !isActualClickEvent(e))) {
			e.preventDefault();
			e.stopImmediatePropagation();
		}

		if (e && this.props.link && this.props.link.event) {
			if (this.props.handleEvent) {
				this.props.handleEvent(e, this.props.link);
			}
			else {
				let customAction = processLinkEvent(this.props.link, e, this.props.mock);
				if (customAction && this.props.onCustomEvent) {
					this.props.onCustomEvent(customAction);
				}
			}
		}

		if (this.props.onAfterClick) {
			this.props.onAfterClick(e);
		}
	}

	trackProps = cache(() => {
		if (!this.props.link) return {};
		let { positionIndex, link } =  this.props;
		let { label, labelId, description, descriptionId } =  link;

		return trackProps({
			[DIMENSION.componentpath]: 'LinkEvent',
			[DIMENSION.text]: label || labelId || description || descriptionId,
			[DIMENSION.position]: positionIndex || 0
		}, this.context);
	});


	static defaultProps = {
		track: {},
		mock: false,
		displayLabel: true,
		displayIcon: true,
		displayImage: true
	}

	render({ children, link, suppressLink = false, trackActive, mock, track, positionIndex, theme, displayLabel, displayIcon, displayImage, isMenuItem, onSubnavCanRender, ...props }) {
		if (!link) return;
		return (

			/*
			onSubnavCanRender comes from the Nav component and exists if this item has a subnav.
			Calling it will cause the subnav to render if it hasn't yet. In order for nav items
			that are restricted to not render their subnav item until they resolve
			we need to use this capability:
			*/
			<Restriction condition={link.restriction} onResolved={onSubnavCanRender}>
				<Localizer>
					<a
						href={(!mock && !link.event && link.url) ? processLinkReplacements(link.url): '#'}
						{...((track && trackActive) && this.trackProps())}
						aria-label={linkEventAriaLabel(link)}
						onClick={this.handleClick}
						{...props}
						class={cx(props.class, link.class)}
						{...linkEventProps(link, mock)}
						ref={this.handleLinkRef}
					>
						{ (children && children.length) && children || (
							<LinkEventView
								link={link}
								theme={theme}
								displayImage={displayImage}
								displayIcon={displayIcon}
								displayLabel={displayLabel}
							/>
						) }
					</a>
				</Localizer>
			</Restriction>
		);
	}
}

const conf = withConfig({
	trackActive: 'headerWidget.track',
	track: 'headerWidget.linkEvent.track',
	mock: 'headerWidget.linkEvent.mock',
	ariaEventLabelDescriptions: 'headerWidget.linkEvent.ariaEventLabelDescriptions'
});
const ConfigLinkEvent = conf(LinkEvent);

export default ConfigLinkEvent;

export function LinkEventList({ links, listType='div', handleEvent, theme, displayLabel, displayIcon, displayImage, positionIndex = 0, ...props }) {
	if (!links) return;
	let ListType = listType;
	let isList = ListType === 'ul' || ListType === 'ol';
	let ListItemType = 'li';
	return (
		<ListType {...props}>
			{links.map((link, i) => isList ? (
				<ListItemType>
					<ConfigLinkEvent
						link={link}
						positionIndex={positionIndex + i}
						handleEvent={handleEvent}
						theme={theme}
						displayImage={displayImage}
						displayIcon={displayIcon}
						displayLabel={displayLabel}
					/>
				</ListItemType>
			) :(
				<ConfigLinkEvent
					link={link}
					positionIndex={positionIndex + i}
					handleEvent={handleEvent}
					theme={theme}
					displayImage={displayImage}
					displayIcon={displayIcon}
					displayLabel={displayLabel}
				/>
			))}
		</ListType>
	);

}
